import type { Idea } from '../api'
import { CHANNEL_LABEL, FORMAT_LABEL, SERIES_LABEL } from '../domain/labels'
import { isPending } from '../state/ideas'
import { ChannelIcon } from './ChannelIcon'
import { ClockIcon, PlayIcon } from './icons'
import { setPosted } from './pickers'
import { PostedMark } from './PostedMark'
import { SeriesDot } from './SeriesDot'

/**
 * The pieces a card on the canvas and a row on the phone are both made of.
 */

/**
 * The line under a title: series and format, then the channels that are not
 * the usual Shorts as small glyphs. Status is not on it: the stage an idea is
 * shown in already says that.
 */
export function IdeaMeta({ idea }: { idea: Idea }) {
  const others = idea.channels.filter((channel) => channel !== 'short')
  return (
    <span className="idea-meta">
      <SeriesDot series={idea.series} size={7} />
      <span>
        {SERIES_LABEL[idea.series]} · {FORMAT_LABEL[idea.format]}
      </span>
      {others.length > 0 && (
        <span
          className="idea-meta-channels"
          role="img"
          aria-label={`Also for ${others.map((channel) => CHANNEL_LABEL[channel]).join(' and ')}`}
        >
          {others.map((channel) => (
            <ChannelIcon key={channel} channel={channel} size={13} />
          ))}
        </span>
      )}
    </span>
  )
}

/** A filmed idea's video, as a small 9:16 frame: ready to play, or still being edited. */
export function VideoThumb({ idea }: { idea: Idea }) {
  const ready = idea.status === 'edited'
  return (
    <span className="video-thumb" data-series={idea.series} data-ready={ready || undefined} aria-hidden="true">
      {ready ? <PlayIcon size={13} /> : <ClockIcon size={15} />}
    </span>
  )
}

/**
 * Where a finished video is out, one ring per video channel, ticked like a
 * to-do. The last one ticked moves the idea to Published; the toast undoes it.
 */
export function VideoRings({ idea }: { idea: Idea }) {
  return (
    <span className="video-rings">
      {idea.channels
        .filter((channel) => channel !== 'linkedin')
        .map((channel) => {
          const posted = idea.posted_to.includes(channel)
          const label = CHANNEL_LABEL[channel]
          return (
            <button
              key={channel}
              type="button"
              className="video-ring"
              aria-pressed={posted}
              aria-label={posted ? `Posted on ${label}. Tap to undo` : `Mark posted on ${label}`}
              disabled={isPending(idea)}
              onClick={(event) => {
                // The ring sits on a card that opens the idea.
                event.preventDefault()
                event.stopPropagation()
                setPosted(idea, channel, !posted)
              }}
            >
              <PostedMark posted={posted} size={16} />
              {label}
            </button>
          )
        })}
    </span>
  )
}
